import { ExtensionBase } from "../extensionBase";
import { UiUtils } from "../../common/uiUtils";
import { ModelApi } from "../../api/modelApi";
import { viewerOptions } from "../../viewer";
import { ViewerUtils } from "./../../common/viewerUtils";
import * as SmartDrawings from "../../smartDrawings";
import { BcfPanelViews } from "./bcfPanelViews";
import { BcfPanelData } from "./bcfPanelData";

export class BcfExtension extends ExtensionBase {
    static extensionName = "BcfExtension";

    private subToolbar: Autodesk.Viewing.UI.ControlGroup;
    private bcfViewsButton: Autodesk.Viewing.UI.Button;
    private bcfDataButton: Autodesk.Viewing.UI.Button;

    private bcf: SmartDrawings.BcfInfo;
    private position = 0;
    private panelViews: BcfPanelViews;
    private panelData: BcfPanelData;
    private originalState: any;
    private lastViewpoint = -1;
    private lastSnapshot = -1;
    private useGhosting = false;

    private ifcIdsToDbIdsOfModels: { [ifcId: string]: { dbId: number, model: Autodesk.Viewing.Model } } = {};

    constructor(viewer: Autodesk.Viewing.GuiViewer3D, options: any) {
        super(viewer, options);

        this.changeGhosting = this.changeGhosting.bind(this);
        this.gotoHome = this.gotoHome.bind(this);
        this.gotoViewpoint = this.gotoViewpoint.bind(this);
        this.nextMarkup = this.nextMarkup.bind(this);
        this.prevMarkup = this.prevMarkup.bind(this);
        this.prevSnapshot = this.prevSnapshot.bind(this);
        this.nextSnapshot = this.nextSnapshot.bind(this);
    }

    static registerExtension() {
        super.registerExtension(BcfExtension.extensionName, BcfExtension);
    }

    load(): boolean {
        console.log(`${BcfExtension.extensionName} is loaded!`);

        this.viewer.addEventListener(
            SmartDrawings.SdEvents.SD_OBJECT_TREE_CREATED, () => this.onAllModelsLoaded());

        return super.load();
    };

    unload(): boolean {
        return super.unload();
    };

    onAllModelsLoaded() {
        this.viewer.removeEventListener(
            SmartDrawings.SdEvents.SD_OBJECT_TREE_CREATED, () => this.onAllModelsLoaded());

        this.originalState = this.viewer.getState();
    }

    async initializeIfcIdMapping() {
        this.ifcIdsToDbIdsOfModels = {};
        let models = this.viewer.getAllModels();
        for (var i = 0; i < models.length; i++) {
            const dbIds = await (models[i] as any).findProperty("GUID");

            await this.getModelIfcIdToDbIdMap(models[i], dbIds);
        }
    }

    async getModelIfcIdToDbIdMap(model: Autodesk.Viewing.Model, dbIds: number[]): Promise<void> {
        model.getBulkProperties(dbIds, { propFilter: ["GLOBALID"] },
            propResults => {
                for (var j = 0; j < propResults.length; j++) {
                    this.ifcIdsToDbIdsOfModels[propResults[j].properties[0].displayValue.toString()] =
                    {
                        dbId: propResults[j].dbId,
                        model: model
                    };
                }
            });
    }

    async onToolbarCreated(): Promise<void> {
        if (!await this.readBcf()) return;

        if (this.bcf == null) return;

        this.panelViews = new BcfPanelViews(this.viewer, this.bcf,
            this.gotoViewpoint,
            this.gotoHome,
            this.changeGhosting);

        this.bcfViewsButton = UiUtils.createButton({
            id: "SmartDrawings.BcfViewsButton",
            tooltip: "Views",
            iconClass: "adsk-icon-box",
            onClick: () => {
                this.panelViews.setVisible(!this.panelViews.isVisible());
            }
        });

        UiUtils.setButtonEnabled(this.bcfViewsButton, false);

        this.panelViews.addVisibilityListener((visible) => this.toggleBcfViewsPanel(visible));

        this.panelData = new BcfPanelData(this.viewer, this.bcf,
            this.gotoViewpoint,
            this.gotoHome,
            this.changeGhosting,
            this.prevMarkup, this.nextMarkup,
            this.prevSnapshot, this.nextSnapshot);

        this.bcfDataButton = UiUtils.createButton({
            id: "SmartDrawings.BcfDataButton",
            tooltip: "BCF",
            iconClass: "adsk-icon-fit-to-view",
            onClick: () => {
                this.panelData.setVisible(!this.panelData.isVisible());
            }
        });

        UiUtils.setButtonEnabled(this.bcfDataButton, false);

        this.panelData.addVisibilityListener((visible) => this.toggleBcfDataPanel(visible));

        this.subToolbar = this.getControlGroup("SmartDrawings.Extensions");
        this.subToolbar.addControl(this.bcfViewsButton);
        this.subToolbar.addControl(this.bcfDataButton);

        await this.initializeIfcIdMapping();

        UiUtils.setButtonEnabled(this.bcfViewsButton, true);
        UiUtils.setButtonEnabled(this.bcfDataButton, true);
    }

    private changeGhosting(use: boolean) {
        this.useGhosting = use;

        if (this.lastViewpoint === -1) return;

        this.resetVisibility();
        this.applyViewpointVisibility(this.lastViewpoint);
    }

    private toggleBcfViewsPanel(visible: boolean) {
        this.bcfViewsButton.setState(visible
            ? Autodesk.Viewing.UI.Button.State.ACTIVE
            : Autodesk.Viewing.UI.Button.State.INACTIVE);

        this.bcfDataButton.setState(visible
            ? Autodesk.Viewing.UI.Button.State.DISABLED
            : Autodesk.Viewing.UI.Button.State.INACTIVE);
    }

    private toggleBcfDataPanel(visible: boolean) {
        if (visible) {
            if (this.lastViewpoint === -1)
                this.lastViewpoint = 0;

            this.setMarkup(this.lastViewpoint);
        }

        this.bcfDataButton.setState(visible
            ? Autodesk.Viewing.UI.Button.State.ACTIVE
            : Autodesk.Viewing.UI.Button.State.INACTIVE);

        this.bcfViewsButton.setState(visible
            ? Autodesk.Viewing.UI.Button.State.DISABLED
            : Autodesk.Viewing.UI.Button.State.INACTIVE);
    }

    private async readBcf(): Promise<boolean> {
        if (this.viewer.model.isPdf()) return false;

        let result;
        try {
            if (viewerOptions.guid)
                result = await ModelApi.getJsonData(viewerOptions.guid, "Bcf_test.json");
            else
                result = await ModelApi.getComboModelBcfData(viewerOptions.comboModelId, "Bcf_test.json");

            if (!result) {
                console.log(result);
                return false;
            }

        } catch (e) {
            return false;
        }

        this.bcf = result.data; 

        for (let n = 0; n < this.bcf.Markups.length; n++) {
            if (!this.bcf.Markups[n].Comment)
                continue;

            this.bcf.Markups[n].Comment.sort((a, b) => {
                const dateA = new Date(a["Date"]);
                const dateB = new Date(b["Date"]);

                // Apparently you have to use getTime or valueOf to compare dates in TS
                return dateB.getTime() - dateA.getTime();
            });
        }

        return true;
    }

    private setNodeVisibility(model: Autodesk.Viewing.Model, dbId: number, show: boolean) {
        if (!this.useGhosting) {
            (this.viewer.impl.visibilityManager as any).setNodeOff(dbId, !show, model);
        }

        if (show)
            this.viewer.show(dbId, model);
        else
            this.viewer.hide(dbId, model);
    }

    private applyViewpointVisibility(index: number) {
        const bcfComponents = this.bcf.Markups[index].Viewpoints[0].VisualizationInfo.Components;
        const visibility = bcfComponents.Visibility;
        const selectedDbIds: number[] = [];

        let models = this.viewer.getAllModels();
        for (var i = 0; i < models.length; i++) {
            ViewerUtils.setModelNodeVisibility(models[i], visibility.DefaultVisibility, this.viewer, this.useGhosting)
        }

        if (visibility.Exceptions) {
            for (let i = 0; i < visibility.Exceptions.length; i++) {
                let exp = visibility.Exceptions[i];
                let value = this.ifcIdsToDbIdsOfModels[exp.IfcGuid];
                if (value)
                    this.setNodeVisibility(value.model, value.dbId, !visibility.DefaultVisibility);
            }
        }
    }

    private resetVisibility() {
        let models = this.viewer.getAllModels();
        for (var i = 0; i < models.length; i++) {
            ViewerUtils.setModelNodeVisibility(models[i], true, this.viewer)
        }

        this.viewer.select(null);
    }

    private gotoViewpoint(markupIndex: number, snapshotIndex: number) {
        this.position = markupIndex;
        this.showViewpoint(this.position, snapshotIndex);
        this.applyViewpointVisibility(markupIndex);
        this.viewer.impl.sceneUpdated(true);

        this.lastViewpoint = markupIndex;
        this.lastSnapshot = snapshotIndex;
    }

    private gotoHome() {
        this.viewer.restoreState(this.originalState);
        this.removeClippingPlanes();
        this.resetVisibility();
        this.viewer.impl.sceneUpdated(true);
        this.lastViewpoint = -1;
        this.lastSnapshot = -1;
    }

    private setMarkup(index: number) {
        const viewpoints = this.bcf.Markups[index].Viewpoints;

        if (viewpoints && viewpoints.length > 0)
            this.gotoViewpoint(index, 0);

        this.panelData.setMarkup(index, 0);
        this.lastViewpoint = index;
    }

    private prevMarkup() {
        const newViewpoint = this.lastViewpoint === 0
            ? this.bcf.Markups.length - 1
            : this.lastViewpoint - 1;

        this.setMarkup(newViewpoint);
    }

    private nextMarkup() {
        const newViewpoint = this.lastViewpoint === this.bcf.Markups.length - 1
            ? 0
            : this.lastViewpoint + 1;

        this.setMarkup(newViewpoint);
    }

    private setSnapshot(index: number) {
        if (index === this.lastSnapshot)
            return;

        this.gotoViewpoint(this.lastViewpoint, index);
        this.panelData.setMarkup(this.lastViewpoint, index);
    }

    private prevSnapshot() {
        const newSnapshot = this.lastSnapshot === 0
            ? this.bcf.Markups[this.lastViewpoint].Viewpoints.length - 1
            : this.lastSnapshot - 1;

        this.setSnapshot(newSnapshot);
    }

    private nextSnapshot() {
        const newSnapshot = this.lastSnapshot === this.bcf.Markups[this.lastViewpoint].Viewpoints.length - 1
            ? 0
            : this.lastSnapshot + 1;

        this.setSnapshot(newSnapshot);
    }

    private showViewpoint(markupIndex: number, snapshotIndex: number) {
        const bcfViewpoint: SmartDrawings.Viewpoint =
            this.bcf.Markups[markupIndex].Viewpoints[snapshotIndex];

        const camera = bcfViewpoint.VisualizationInfo.PerspectiveCamera ??
            bcfViewpoint.VisualizationInfo.OrthogonalCamera;

        const position = new THREE.Vector3(
            camera.CameraViewPoint.X,
            camera.CameraViewPoint.Y,
            camera.CameraViewPoint.Z
        );

        const direction = new THREE.Vector3(
            camera.CameraDirection.X,
            camera.CameraDirection.Y,
            camera.CameraDirection.Z
        );

        const target = position.clone().add(direction);

        this.viewer.navigation.setView(position, target);

        this.viewer.navigation.setCameraUpVector(
            new THREE.Vector3(
                camera.CameraUpVector.X,
                camera.CameraUpVector.Y,
                Math.abs(camera.CameraUpVector.Z)
            )
        );

        if (bcfViewpoint.VisualizationInfo.PerspectiveCamera) {
            this.viewer.navigation.toPerspective();
            this.viewer.navigation.setVerticalFov(bcfViewpoint.VisualizationInfo.PerspectiveCamera.FieldOfView, false);
        } else {
            this.viewer.navigation.toOrthographic();
            this.viewer.impl.setViewFromCamera({
                aspect: (this.viewer.getCamera() as any).aspect,
                isPerspective: false,
                position: position,
                target: target,
                up: new THREE.Vector3(
                    camera.CameraUpVector.X,
                    camera.CameraUpVector.Y,
                    Math.abs(camera.CameraUpVector.Z)
                ),
                orthoScale: bcfViewpoint.VisualizationInfo.OrthogonalCamera.ViewToWorldScale
            } as any);
        }

        this.viewer.navigation.setPivotPoint(target);

        const clippingPlanes = bcfViewpoint.VisualizationInfo.ClippingPlanes;

        if (clippingPlanes && clippingPlanes.length > 0) {
            this.applyClipPlanes(clippingPlanes);
        }
        else {
            this.removeClippingPlanes();
        }
    }

    private applyClipPlanes(clipPlanes: SmartDrawings.ClippingPlane[]) {
        let planeVecs: THREE.Vector4[] = [];

        for (var i = 0; i < clipPlanes.length; i++) {
            const normal = new THREE.Vector3(
                clipPlanes[i].Direction.X,
                clipPlanes[i].Direction.Y,
                clipPlanes[i].Direction.Z
            );

            const position = new THREE.Vector3(
                clipPlanes[i].Location.X,
                clipPlanes[i].Location.Y,
                clipPlanes[i].Location.Z
            );

            const distance = -1 * position.dot(normal);
            const plane = new THREE.Plane(normal, distance);

            planeVecs.push(new THREE.Vector4(plane.normal.x, plane.normal.y, plane.normal.z, plane.constant));
        }

        this.viewer.setCutPlanes(planeVecs);
    }

    private removeClippingPlanes() {
        this.viewer.setCutPlanes(null);
    }
}